import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEmailVerification, useAuth } from '@shared'
import { setAccessToken } from '@/lib/authToken'

type Status =
  | { kind: 'verifying-email' }
  | { kind: 'completing-oauth' }
  | { kind: 'verified' }
  | { kind: 'error'; message: string }

const SUCCESS_DWELL_MS = 1500

export const AuthCallback = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { hideVerificationOverlay } = useEmailVerification()
  const { checkAuth } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000'

  const [status, setStatus] = useState<Status>({ kind: 'completing-oauth' })
  // React StrictMode double-invokes effects in dev. Verification tokens are
  // single-use server-side, so the second call returns "already verified"
  // and the catch block clobbers the successful first call's redirect.
  // Guard with a ref so the effect body runs at most once per mount cycle.
  const initiatedRef = useRef(false)

  const handleOAuthError = useCallback((errorParam: string, email: string | null) => {
    console.error('OAuth error:', errorParam)

    if (errorParam === 'LINK_REQUIRED') {
      const decodedEmail = email ? decodeURIComponent(email) : ''
      navigate('/sign-in', {
        replace: true,
        state: {
          error: decodedEmail
            ? `An account with ${decodedEmail} already exists. Sign in with your password, then link Google from Account → Settings.`
            : 'An account with this email already exists. Sign in with your password, then link Google from Account → Settings.',
          prefillEmail: decodedEmail || undefined,
        },
      })
      return
    }


    const errorMessage = decodeURIComponent(errorParam)
    if (errorMessage.includes('already exists')) {
      navigate('/sign-up', {
        replace: true,
        state: {
          error: errorMessage,
          showGoogleError: true
        }
      })
    } else {
      navigate('/sign-in', {
        replace: true,
        state: { error: errorMessage }
      })
    }
  }, [navigate])

  const verifyEmail = useCallback(async (token: string) => {
    setStatus({ kind: 'verifying-email' })
    try {
      const response = await fetch(`${apiUrl}/auth/verify-email`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Verification failed')
      }

      const data = await response.json()
      if (!data.accessToken) {
        throw new Error('Verification succeeded but no session was issued')
      }

      // Phase 2 auth-hardening: token in memory only; the refresh cookie
      // was just set by the backend on the same response.
      setAccessToken(data.accessToken)
      // Hydrate AuthContext.user from the new token before navigating —
      // otherwise the dashboard sees a null user and bounces us to /sign-in.
      await checkAuth()
      hideVerificationOverlay()
      setStatus({ kind: 'verified' })

      // Brief dwell so the user actually sees the success state before
      // we whisk them to the dashboard.
      setTimeout(() => navigate('/dashboard', { replace: true }), SUCCESS_DWELL_MS)
    } catch (error) {
      console.error('Email verification error:', error)
      const message =
        error instanceof Error ? error.message : 'Verification failed'
      navigate('/sign-in', {
        replace: true,
        state: { error: `${message}. Please try signing in or request a new verification email.` },
      })
    }
  }, [apiUrl, checkAuth, hideVerificationOverlay, navigate])

  const handleGoogleLogin = useCallback(async (token: string) => {
    setStatus({ kind: 'completing-oauth' })
    try {
      // Phase 2: the access token is in the URL fragment (not query). The
      // refresh cookie was set by the backend on the same redirect. Store
      // the access token in memory and bootstrap the AuthContext user.
      setAccessToken(token)

      const userData = await checkAuth()

      if (userData) {
        if (userData.isVerified) {
          hideVerificationOverlay()
        }
        navigate('/dashboard', { replace: true })
      } else {
        throw new Error('Failed to fetch user profile')
      }
    } catch (error) {
      console.error('Google OAuth error:', error)
      navigate('/sign-in', { replace: true })
    }
  }, [checkAuth, hideVerificationOverlay, navigate])

  // Helper: read a single field from the URL hash (e.g. `#token=abc`).
  const hashParam = (name: string): string | null => {
    const raw = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash
    const params = new URLSearchParams(raw)
    return params.get(name)
  }

  useEffect(() => {
    if (initiatedRef.current) return
    initiatedRef.current = true

    // Google OAuth callback now carries the access token in the URL fragment
    // (Phase 2 — fragment isn't logged on the server side or sent as a
    // Referer). Verification still uses the query param since the token is
    // single-use and travels through email links.
    const token = hashParam('token') ?? searchParams.get('token')
    const mfaChallenge = hashParam('mfa')
    const verificationToken = searchParams.get('verification_token')
    const error = searchParams.get('error')

    if (error) {
      handleOAuthError(error, searchParams.get('email'))
    } else if (verificationToken) {
      verifyEmail(verificationToken)
    } else if (mfaChallenge) {
      // Phase 4 — Google flow proved identity but the account also requires
      // a TOTP code. The challenge JWT lives 5 minutes; the user has to
      // exchange it for a real session via /sign-in/mfa.
      navigate('/sign-in/mfa', {
        replace: true,
        state: { challengeToken: mfaChallenge },
      })
    } else if (token) {
      handleGoogleLogin(token)
    } else {
      navigate('/sign-in', { replace: true })
    }
  }, [searchParams, navigate, handleOAuthError, verifyEmail, handleGoogleLogin])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        {status.kind === 'verified' ? (
          <>
            <div className="mx-auto h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
              <svg
                className="h-7 w-7 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={3}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="mt-4 text-gray-900 font-medium">Email verified</p>
            <p className="mt-1 text-sm text-gray-600">Signing you in…</p>
          </>
        ) : (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">
              {status.kind === 'verifying-email'
                ? 'Verifying your email…'
                : 'Completing sign in…'}
            </p>
          </>
        )}
      </div>
    </div>
  )
}
